import { useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import useDebouncedValue from '../../hooks/useDebouncedValue';

/**
 * Controlled per-slot BSA number field for the quick game creator.
 * Looks up the player by BSA number (GET /api/player/bsa/:bsaNumber) and
 * reports the result up via onResolved. Unknown BSA numbers block the slot.
 *
 * @param {String} value - current BSA input (controlled)
 * @param {Object|null} player - resolved player object ({name, surname, ...})
 * @param {'idle'|'loading'|'valid'|'invalid'} status - lookup status
 * @param {Function} onResolved - (bsaNumber, player|null) called when a lookup settles
 * @param {Function} onChange - (rawValue) called on every keystroke
 */
const BsaPlayerField = ({
  label,
  value,
  player,
  status,
  onResolved,
  onChange,
  disabled = false,
  error = false
}) => {
  const debouncedValue = useDebouncedValue(value, 450);
  const abortRef = useRef(null);
  const latestValueRef = useRef(value);
  const [errorMessage, setErrorMessage] = useState(null);

  // Track the latest raw input so slow lookups for an older value can't
  // overwrite what the user is currently typing.
  latestValueRef.current = value;

  useEffect(() => {
    const trimmed = String(debouncedValue || '').trim();

    if (abortRef.current) {
      abortRef.current.abort();
    }

    if (!trimmed) {
      setErrorMessage(null);
      onResolved('', null);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Ignore results if the user has since changed the input.
    const isStale = () => String(latestValueRef.current ?? '').trim() !== trimmed;

    onResolved(trimmed, null, 'loading');

    fetch(`/api/player/bsa/${encodeURIComponent(trimmed)}`, { signal: controller.signal })
      .then(async (res) => {
        if (isStale()) return;

        if (res.status === 404) {
          setErrorMessage(`No player found for BSA ${trimmed}`);
          onResolved(trimmed, null, 'invalid');
          return;
        }

        if (!res.ok) {
          setErrorMessage('Could not look up player, try again');
          onResolved(trimmed, null, 'invalid');
          return;
        }

        const playerData = await res.json();
        if (isStale()) return;
        setErrorMessage(null);
        onResolved(trimmed, playerData, 'valid');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        if (isStale()) return;
        setErrorMessage('Could not look up player, try again');
        onResolved(trimmed, null, 'invalid');
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const showLoading = status === 'loading';
  const showChip = status === 'valid' && player;

  return (
    <Box display={'flex'} flexDirection={'column'} gap={0.5}>
      <TextField
        label={label}
        value={value}
        onChange={(event) => {
          const next = event.target.value.replace(/\D/g, '');
          setErrorMessage(null);
          onChange(next);
        }}
        disabled={disabled}
        error={error || status === 'invalid'}
        inputProps={{ inputMode: 'numeric', autoComplete: 'off', maxLength: 6 }}
        InputProps={{
          endAdornment: showLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ) : null
        }}
        fullWidth
        size="small"
      />

      {showChip && (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`${player.name} ${player.surname}`}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      {errorMessage && (
        <Typography variant="caption" color="error">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default BsaPlayerField;
